import merge from 'lodash/merge.js';
import tinycolor from 'tinycolor2';
import { BACKGROUND_COLOR_KEYS, BASIC_PALETTE_KEYS, BORDER_COLOR_KEYS, BOX_SHADOW_KEYS, TEXT_COLOR_KEYS, UX_COLOR_SYSTEMS, } from './constants/index.js';
const clamp = (number, min, max) => Math.min(Math.max(number, min), max);
const clampColorValue = (value) => clamp(value, 0, 255);
const shiftColor = (color, amount, 
// adding this as a future option, since I have all the logic worked out
method) => {
    const colorAsNumber = parseInt(color.slice(1), 16);
    const baseR = colorAsNumber >> 16;
    const baseG = (colorAsNumber >> 8) & 0x00ff;
    const baseB = colorAsNumber & 0x0000ff;
    const operand = method === 'absolute'
        ? (value) => value + amount
        : (value) => value + value * amount;
    const [shiftedR, shiftedG, shiftedB] = [baseR, baseG, baseB].map((value) => clampColorValue(operand(value)));
    const shiftedHex = (shiftedB | (shiftedG << 8) | (shiftedR << 16))
        .toString(16)
        .padStart(6, '0');
    return `#${shiftedHex}`;
};
const getPaletteColor = (palettes, key) => {
    const [palette, subkey] = key.split('.');
    return palettes[palette][subkey];
};
const mapSystemPalettes = (mergedColorTheme, brightnessPalette) => {
    const systemEntries = UX_COLOR_SYSTEMS.map((system) => {
        const paletteEntries = BASIC_PALETTE_KEYS.map((key) => [
            key,
            getPaletteColor(mergedColorTheme.palettes, mergedColorTheme.maps[brightnessPalette][system][key]),
        ]);
        return [system, Object.fromEntries(paletteEntries)];
    });
    return Object.fromEntries(systemEntries);
};
export const mergeColors = (baseColors, overrideColors, palette) => {
    const brandPrimaryColor = overrideColors.brand?.primary ?? baseColors.brand.primary;
    const mergedColorTheme = merge(baseColors, overrideColors);
    const backgroundColor = brandPrimaryColor;
    // HSL-based strategy for finding accessible colors
    const findAccessibleOverPrimary = (bgColor) => {
        const bg = tinycolor(bgColor);
        const bgHsl = bg.toHsl();
        const bgLuminance = bg.getLuminance();
        // Calculate target luminance for 4.5:1 contrast ratio
        const targetLuminance = bgLuminance > 0.5
            ? Math.max(0.05, (bgLuminance + 0.05) / 4.5 - 0.05) // Dark text on light bg
            : Math.min(0.95, (bgLuminance + 0.05) * 4.5 - 0.05); // Light text on dark bg
        // Strategy 1: Generate tints/shades of the background color with precise luminance
        const hslCandidates = [
            { h: bgHsl.h, s: bgHsl.s, l: targetLuminance }, // Same hue/saturation, precise luminance
            { h: bgHsl.h, s: Math.min(bgHsl.s + 0.2, 1), l: targetLuminance }, // More saturated
            { h: bgHsl.h, s: Math.max(bgHsl.s - 0.2, 0), l: targetLuminance }, // Less saturated
            { h: (bgHsl.h + 30) % 360, s: bgHsl.s, l: targetLuminance }, // Adjacent hue
            { h: (bgHsl.h - 30 + 360) % 360, s: bgHsl.s, l: targetLuminance }, // Adjacent hue (other direction)
            // More extreme luminance values for better contrast
            { h: bgHsl.h, s: bgHsl.s, l: bgLuminance > 0.5 ? 0.05 : 0.95 }, // Very dark/light
            { h: bgHsl.h, s: 0, l: targetLuminance }, // Desaturated version
            { h: bgHsl.h, s: 0, l: bgLuminance > 0.5 ? 0.1 : 0.9 }, // Desaturated extreme
        ];
        for (const hsl of hslCandidates) {
            const candidate = tinycolor(hsl).toHexString();
            if (tinycolor.readability(bgColor, candidate) >= 4.5) {
                return candidate;
            }
        }
        // Strategy 2: Complementary and triadic colors with extreme luminance
        const complementaryHsl = [
            { h: (bgHsl.h + 180) % 360, s: 0.8, l: targetLuminance }, // Complementary
            { h: (bgHsl.h + 120) % 360, s: 0.6, l: targetLuminance }, // Triadic
            { h: (bgHsl.h + 240) % 360, s: 0.6, l: targetLuminance }, // Triadic
            // More extreme versions
            { h: (bgHsl.h + 180) % 360, s: 0.8, l: bgLuminance > 0.5 ? 0.05 : 0.95 }, // Complementary extreme
            { h: (bgHsl.h + 120) % 360, s: 0.6, l: bgLuminance > 0.5 ? 0.05 : 0.95 }, // Triadic extreme
            { h: (bgHsl.h + 240) % 360, s: 0.6, l: bgLuminance > 0.5 ? 0.05 : 0.95 }, // Triadic extreme
        ];
        for (const hsl of complementaryHsl) {
            const candidate = tinycolor(hsl).toHexString();
            if (tinycolor.readability(bgColor, candidate) >= 4.5) {
                return candidate;
            }
        }
        // Strategy 3: High contrast grays (fallback)
        const highContrastGrays = [
            '#1a1a1a',
            '#333333',
            '#4a4a4a',
            '#666666',
            '#999999',
            '#cccccc',
            '#e5e5e5',
            '#f8f8f8',
        ];
        for (const gray of highContrastGrays) {
            if (tinycolor.readability(bgColor, gray) >= 4.5)
                return gray;
        }
        // Strategy 4: Pure black/white (final fallback)
        const pureBlack = '#000000';
        const pureWhite = '#ffffff';
        if (tinycolor.readability(bgColor, pureBlack) >= 4.5)
            return pureBlack;
        if (tinycolor.readability(bgColor, pureWhite) >= 4.5)
            return pureWhite;
        // Ultimate fallback
        return bgLuminance > 0.5 ? pureBlack : pureWhite;
    };
    const accessibleOverPrimary = findAccessibleOverPrimary(backgroundColor);
    const accessibleOverBackground = tinycolor.isReadable(brandPrimaryColor, getPaletteColor(mergedColorTheme.palettes, mergedColorTheme.maps[palette].background.primary), {
        level: 'AA',
        size: 'small',
    });
    return {
        brand: {
            primary: brandPrimaryColor,
            light: shiftColor(brandPrimaryColor, 0.12, 'percentage'),
            dark: shiftColor(brandPrimaryColor, -0.12, 'percentage'),
            accessibleOverPrimary,
            accessibleOverBackground: accessibleOverBackground
                ? brandPrimaryColor
                : '#000000',
        },
        text: Object.fromEntries(TEXT_COLOR_KEYS.map((key) => [
            key,
            getPaletteColor(mergedColorTheme.palettes, mergedColorTheme.maps[palette].text[key]),
        ])),
        background: Object.fromEntries(BACKGROUND_COLOR_KEYS.map((key) => [
            key,
            getPaletteColor(mergedColorTheme.palettes, mergedColorTheme.maps[palette].background[key]),
        ])),
        border: Object.fromEntries(BORDER_COLOR_KEYS.map((key) => [
            key,
            getPaletteColor(mergedColorTheme.palettes, mergedColorTheme.maps[palette].border[key]),
        ])),
        boxShadow: Object.fromEntries(BOX_SHADOW_KEYS.map((key) => [
            key,
            mergedColorTheme.maps[palette].boxShadow[key],
        ])),
        ...mapSystemPalettes(mergedColorTheme, palette),
    };
};
//# sourceMappingURL=mergeColors.js.map